import type { ColorMode } from "../types/fluid-types.js"

export class ColorManager {
	private currentHue: number = Math.random()
	private targetHue: number = Math.random()
	private colorUpdateTimer: number = 0
	private fixedColor: [number, number, number] | null = null
	private colorMode: ColorMode
	private customColors: [number, number, number][]

	constructor(
		colorMode: ColorMode = "rainbow",
		customColors: [number, number, number][] = []
	) {
		this.colorMode = colorMode
		this.customColors = customColors
	}

	public updateColors(
		dt: number,
		colorful: boolean,
		colorUpdateSpeed: number,
		colorTransitionSpeed: number
	): [number, number, number] {
		if (!colorful) {
			return [1, 1, 1] // White when not colorful
		}

		// If a fixed color is set, return it directly
		if (this.fixedColor) {
			return this.fixedColor
		}

		this.colorUpdateTimer += dt * colorUpdateSpeed

		// Smoothly interpolate between current and target hue
		const hueStep = colorTransitionSpeed * dt
		if (Math.abs(this.targetHue - this.currentHue) > hueStep) {
			if (this.targetHue > this.currentHue) {
				this.currentHue += hueStep
			} else {
				this.currentHue -= hueStep
			}
		} else {
			this.currentHue = this.targetHue
		}

		// Generate new target hue occasionally
		if (this.colorUpdateTimer >= 1) {
			this.colorUpdateTimer = 0
			this.targetHue = this.generateNewTargetHue()
		}

		return this.generateColor(this.currentHue)
	}

	private generateNewTargetHue(): number {
		switch (this.colorMode) {
			case "rainbow":
				return Math.random()

			case "monochrome":
				// Stay within a narrow hue range for monochrome effect
				const baseHue = 0.6 // Blue-ish base
				return baseHue + (Math.random() - 0.5) * 0.1

			case "custom":
				if (this.customColors.length > 0) {
					const randomColor =
						this.customColors[
							Math.floor(Math.random() * this.customColors.length)
						]
					return this.rgbToHue(randomColor)
				}
				return Math.random()

			default:
				return Math.random()
		}
	}

	private generateColor(hue: number): [number, number, number] {
		switch (this.colorMode) {
			case "rainbow":
				return this.hsvToRgb(hue, 1.0, 1.0)

			case "monochrome":
				return this.hsvToRgb(hue, 0.8, 1.0)

			case "custom":
				if (this.customColors.length > 0) {
					const randomColor =
						this.customColors[
							Math.floor(Math.random() * this.customColors.length)
						]
					return randomColor
				}
				return this.hsvToRgb(hue, 1.0, 1.0)

			default:
				return this.hsvToRgb(hue, 1.0, 1.0)
		}
	}

	private hsvToRgb(h: number, s: number, v: number): [number, number, number] {
		let r: number, g: number, b: number
		const i = Math.floor(h * 6)
		const f = h * 6 - i
		const p = v * (1 - s)
		const q = v * (1 - f * s)
		const t = v * (1 - (1 - f) * s)

		switch (i % 6) {
			case 0:
				r = v
				g = t
				b = p
				break
			case 1:
				r = q
				g = v
				b = p
				break
			case 2:
				r = p
				g = v
				b = t
				break
			case 3:
				r = p
				g = q
				b = v
				break
			case 4:
				r = t
				g = p
				b = v
				break
			case 5:
				r = v
				g = p
				b = q
				break
			default:
				r = g = b = 0
		}

		return [r, g, b]
	}

	private rgbToHue(rgb: [number, number, number]): number {
		const [r, g, b] = rgb
		const max = Math.max(r, g, b)
		const min = Math.min(r, g, b)
		const delta = max - min

		if (delta === 0) return 0

		let hue: number
		if (max === r) {
			hue = ((g - b) / delta) % 6
		} else if (max === g) {
			hue = (b - r) / delta + 2
		} else {
			hue = (r - g) / delta + 4
		}

		hue /= 6
		if (hue < 0) hue += 1

		return hue
	}

	public setColorMode(mode: ColorMode): void {
		this.colorMode = mode
	}

	public setCustomColors(colors: [number, number, number][]): void {
		this.customColors = colors
	}

	public setFixedColor(color: [number, number, number] | null): void {
		this.fixedColor = color
	}

	public clearFixedColor(): void {
		this.fixedColor = null
	}

	public generateRandomColor(): [number, number, number] {
		// If a fixed color is set, return it
		if (this.fixedColor) {
			return this.fixedColor
		}
		return this.generateColor(Math.random())
	}

	public reset(): void {
		this.currentHue = Math.random()
		this.targetHue = Math.random()
		this.colorUpdateTimer = 0
	}
}

// Utility functions for color manipulation
export function hexToRgb(hex: string): [number, number, number] | null {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
	return result
		? [
				parseInt(result[1], 16) / 255,
				parseInt(result[2], 16) / 255,
				parseInt(result[3], 16) / 255,
		  ]
		: null
}

export function rgbToHex(rgb: [number, number, number]): string {
	const [r, g, b] = rgb.map((c) => Math.round(c * 255))
	return `#${r.toString(16).padStart(2, "0")}${g
		.toString(16)
		.padStart(2, "0")}${b.toString(16).padStart(2, "0")}`
}

export function parseColorArray(
	colorString: string
): [number, number, number][] {
	try {
		const parsed = JSON.parse(colorString)
		if (Array.isArray(parsed)) {
			return parsed.filter(
				(color) =>
					Array.isArray(color) &&
					color.length === 3 &&
					color.every((c) => typeof c === "number" && c >= 0 && c <= 1)
			)
		}
	} catch (e) {
		console.warn("Failed to parse custom colors:", e)
	}
	return []
}

// CSS named colors mapping to RGB values (normalized 0-1)
const CSS_NAMED_COLORS: Record<string, [number, number, number]> = {
	// Basic colors
	black: [0, 0, 0],
	white: [1, 1, 1],
	red: [1, 0, 0],
	green: [0, 0.5, 0],
	blue: [0, 0, 1],
	yellow: [1, 1, 0],
	cyan: [0, 1, 1],
	magenta: [1, 0, 1],
	orange: [1, 0.647, 0],
	purple: [0.5, 0, 0.5],
	pink: [1, 0.753, 0.796],
	brown: [0.647, 0.165, 0.165],
	gray: [0.5, 0.5, 0.5],
	grey: [0.5, 0.5, 0.5],

	// Extended colors
	lime: [0, 1, 0],
	navy: [0, 0, 0.5],
	maroon: [0.5, 0, 0],
	olive: [0.5, 0.5, 0],
	teal: [0, 0.5, 0.5],
	silver: [0.753, 0.753, 0.753],
	aqua: [0, 1, 1],
	fuchsia: [1, 0, 1],

	// Popular web colors
	crimson: [0.863, 0.078, 0.235],
	gold: [1, 0.843, 0],
	indigo: [0.294, 0, 0.51],
	coral: [1, 0.498, 0.314],
	salmon: [0.98, 0.502, 0.447],
	violet: [0.933, 0.51, 0.933],
	turquoise: [0.251, 0.878, 0.816],
	khaki: [0.941, 0.902, 0.549],
	plum: [0.867, 0.627, 0.867],
	orchid: [0.855, 0.439, 0.839],
}

/**
 * Parse a CSS color string (hex or named color) to RGB values normalized to 0-1
 */
export function parseCSSColor(
	colorString: string
): [number, number, number] | null {
	if (!colorString || typeof colorString !== "string") {
		return null
	}

	const trimmed = colorString.trim().toLowerCase()

	// Try hex color first
	if (trimmed.startsWith("#")) {
		return hexToRgb(trimmed)
	}

	// Try named color
	if (CSS_NAMED_COLORS[trimmed]) {
		return CSS_NAMED_COLORS[trimmed]
	}

	// Try parsing as hex without #
	if (/^[0-9a-f]{6}$/i.test(trimmed)) {
		return hexToRgb("#" + trimmed)
	}

	// Try 3-digit hex
	if (/^[0-9a-f]{3}$/i.test(trimmed)) {
		const expanded = trimmed
			.split("")
			.map((c) => c + c)
			.join("")
		return hexToRgb("#" + expanded)
	}

	return null
}
